/**
 * Minimal fetch wrapper for the cabinet API. Handles:
 *
 *  - prefixing every URL with `import.meta.env.VITE_API_BASE_URL` (defaults
 *    to `/api` so Caddy + the dev proxy don't need any reconfiguration);
 *  - injecting the JWT bearer token when present;
 *  - normalizing errors to `ApiError` so callers don't unwrap them inline.
 *
 * Deliberately small — we add a method when a route needs it, never before.
 */
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/types';

import { readToken, writeToken } from './storage.js';

const BASE = import.meta.env.VITE_API_BASE_URL ?? '/api';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const token = readToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'same-origin',
    });
  } catch (err) {
    throw new ApiError(0, 'network', `Сеть недоступна: ${(err as Error).message}`);
  }

  if (res.status === 401) {
    writeToken(null);
  }

  if (res.status === 204) return undefined as T;

  let payload: unknown = null;
  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    try {
      payload = await res.json();
    } catch {
      payload = null;
    }
  }

  if (!res.ok) {
    const err = (payload as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(
      res.status,
      err?.code ?? `http_${res.status}`,
      err?.message ?? `HTTP ${res.status}`,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  delete: <T = void>(path: string) => request<T>('DELETE', path),
};

// ---------------------------------------------------------------------------
// Typed call sites — keep all paths in one place so refactors are mechanical.
// ---------------------------------------------------------------------------

export interface MeResponse {
  user: { id: string; name: string; note: string | null };
  device: { id: string; name: string; createdAt: string; lastSeenAt: string };
}

export interface HashLoginResponse {
  requestId: string;
  status: 'pending';
  expiresAt: string;
}

export interface ApprovalPollPending {
  status: 'pending';
}
export interface ApprovalPollRejected {
  status: 'rejected';
  reason?: string;
}
export interface ApprovalPollExpired {
  status: 'expired';
  reason?: string;
}
export interface ApprovalPollApprovedLogin {
  status: 'approved';
  approvalId: string;
  registrationOptions: PublicKeyCredentialCreationOptionsJSON;
}
export type ApprovalPollLogin =
  | ApprovalPollPending
  | ApprovalPollRejected
  | ApprovalPollExpired
  | ApprovalPollApprovedLogin;

export interface WebauthnVerifyResponse {
  token: string;
  deviceId: string;
  user: { id: string; name: string };
}

export type VpnProtocol = 'awg' | 'vless';

export interface SerializedConfig {
  id: string;
  name: string;
  status: 'ACTIVE' | 'REVOKED';
  /**
   * Optional in the type so a cached payload from an older API — which had no
   * such field — still reads as AmneziaWG rather than `undefined`.
   */
  protocol?: VpnProtocol;
  /** `null` for vless: no tunnel address is assigned. */
  clientIp: string | null;
  publicKey: string;
  /** VPN server this config lives on. */
  serverId: string;
  /**
   * Whether the byte counters below are measurements. False for vless until
   * Xray's StatsService is enabled — rendering its zeros as a reading would
   * tell the user their config is dead when it is fine.
   */
  statsAvailable?: boolean;
  createdAt: string;
  revokedAt: string | null;
  lastHandshakeAt: string | null;
  bytesReceived: string;
  bytesSent: string;
}

/** AmneziaWG credentials. `protocol` is absent on payloads from older APIs. */
export interface AwgClientConfig {
  protocol?: 'awg';
  publicKey: string;
  privateKey: string;
  presharedKey: string;
  clientIp: string;
  serverPublicKey: string;
  endpoint: string;
  obfuscation: Record<string, string>;
  /** AmneziaWG-native `[Interface]/[Peer]` text. */
  text: string;
  /**
   * AmneziaVPN-app `vpn://...` URL (one-tap import). Optional in the type so
   * cached payloads minted by older API versions don't break the cabinet.
   */
  vpnUrl?: string;
  /**
   * `nevodapp://import#…` — одноразовый импорт в наше приложение. Optional in
   * the type so cached payloads from an older API still render.
   */
  appImportUrl?: string;
}

/** VLESS credentials. The UUID lives only inside `vlessUrl`. */
export interface VlessClientConfig {
  protocol: 'vless';
  email: string;
  endpoint: string;
  serverName: string;
  fingerprint: string;
  flow: string;
  vlessUrl: string;
  /**
   * `nevodapp://import#…` — one-tap import into our own mobile client.
   * Optional in the type so cached payloads from an older API still render.
   */
  appImportUrl?: string;
}

export type SerializedClientConfig = AwgClientConfig | VlessClientConfig;

/** Narrowing helper — the absent-`protocol` case means a legacy awg payload. */
export function isVlessClient(c: SerializedClientConfig): c is VlessClientConfig {
  return c.protocol === 'vless';
}

/**
 * One element of a create response. Single-device requests come back as a
 * one-item `configs` array so the cabinet handles N=1 and N>1 the same way.
 */
export interface ConfigEntry {
  config: SerializedConfig;
  client: SerializedClientConfig;
}

export interface ConfigCreateResponse {
  configs: ConfigEntry[];
}

export interface ReissueResponse {
  config: SerializedConfig;
  client: SerializedClientConfig;
}

export interface ServerOption {
  id: string;
  label: string;
  /** Absent on older API bundles — treat as awg. */
  protocol?: VpnProtocol;
}

export interface ServersResponse {
  servers: ServerOption[];
  defaultServerId: string;
}

// ---------------------------------------------------------------------------

export const auth = {
  hashLogin: (input: { hash: string; fingerprint: string; deviceName: string }) =>
    api.post<HashLoginResponse>('/auth/hash', input),
  pollRequest: (id: string) => api.get<ApprovalPollLogin>(`/auth/requests/${id}`),
  webauthnRegisterVerify: (input: { approvalId: string; response: RegistrationResponseJSON }) =>
    api.post<WebauthnVerifyResponse>('/auth/webauthn/register/verify', input),
  webauthnAssertOptions: () =>
    api.post<PublicKeyCredentialRequestOptionsJSON>('/auth/webauthn/assert/options'),
  webauthnAssertVerify: (response: AuthenticationResponseJSON) =>
    api.post<WebauthnVerifyResponse>('/auth/webauthn/assert/verify', { response }),
};

export const me = {
  get: () => api.get<MeResponse>('/me'),
  deleteDevice: () => api.delete<void>('/me/device'),
  listServers: () => api.get<ServersResponse>('/me/servers'),
  listConfigs: () => api.get<{ items: SerializedConfig[] }>('/me/configs'),
  /**
   * Always submits a `deviceNames` array — even single-device requests go
   * through the batch pipeline so the cabinet has one polling shape to
   * worry about. `serverId` picks the AWG host; omitted → API default.
   */
  createConfigs: (deviceNames: string[], serverId?: string) =>
    api.post<ConfigCreateResponse>('/me/configs', {
      deviceNames,
      ...(serverId ? { serverId } : {}),
    }),
  reissueConfig: (id: string) => api.post<ReissueResponse>(`/me/configs/${id}/reissue`),
  deleteConfig: (id: string) => api.delete<void>(`/me/configs/${id}`),
};

/** One-time pairing code shown in the cabinet and typed into the app. */
export interface PairingCodeResponse {
  code: string;
  expiresAt: string;
}

/** An installation of our own mobile client bound to the user. */
export interface AppInstance {
  id: string;
  label: string;
  platform: 'ios' | 'android';
  createdAt: string;
  lastSeenAt: string;
  lastIp: string | null;
}

/** Статус вручную залитой APK-сборки — `size` недоступен, пока файла нет. */
export type AndroidBuild = { available: false } | { available: true; size: number; updatedAt: string };

export const appLink = {
  createPairingCode: () => api.post<PairingCodeResponse>('/me/app/pairing'),
  listInstances: () => api.get<{ items: AppInstance[] }>('/me/app/instances'),
  revokeInstance: (id: string) => api.delete<void>(`/me/app/instances/${id}`),
  androidBuild: () => api.get<AndroidBuild>('/me/app/android'),
  androidTicket: () => api.post<{ ticket: string }>('/me/app/android/ticket'),
  // Ссылка, а не fetch: 67 МБ через Blob легли бы в память вкладки, а качают
  // с телефона. Заголовок Authorization обычная навигация не несёт — поэтому
  // удостоверение уезжает одноразовым билетом в адресе.
  androidUrl: (ticket: string) => `${BASE}/app/android?ticket=${encodeURIComponent(ticket)}`,
};
